/**
 * Response Builder Questions Repository
 * 
 * Handles rb_questions collection operations with immutable revision model.
 * Phase D4-D5 - Deterministic extraction and question review/correction
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy as firestoreOrderBy,
  Timestamp,
  type Firestore,
} from 'firebase/firestore';

import type {
  RbQuestion,
  CreateQuestionInput,
  EditQuestionInput,
  SplitQuestionInput,
  MergeQuestionsInput,
  IgnoreQuestionInput,
  QuestionState,
} from './types/rb';

/**
 * Create a question node from extraction
 * 
 * Phase D4: Extraction creates nodes with:
 * - operation='extract'
 * - lineageRootId = id
 * - state='active', isActive=true
 */
export async function createExtractedQuestion(
  db: Firestore,
  questionId: string,
  input: CreateQuestionInput
): Promise<RbQuestion> {
  const now = Timestamp.now();
  const questionRef = doc(db, 'rb_questions', questionId);

  const question: Omit<RbQuestion, 'id'> = {
    workspaceId: input.workspaceId,
    projectId: input.projectId,
    documentId: input.documentId,
    orderIndex: input.orderIndex,
    orderSubIndex: input.orderSubIndex,
    extractedText: input.extractedText,
    questionText: input.questionText,
    state: 'active',
    isActive: true,
    operation: 'extract',
    lineageRootId: questionId, // Root of its own lineage chain
    createdAt: now,
    createdByUserId: input.createdByUserId,
  };

  await setDoc(questionRef, question);

  return {
    id: questionId,
    ...question,
  };
}

/**
 * Get question by ID
 */
export async function getQuestion(
  db: Firestore,
  questionId: string
): Promise<RbQuestion | null> {
  const questionRef = doc(db, 'rb_questions', questionId);
  const questionSnap = await getDoc(questionRef);

  if (!questionSnap.exists()) {
    return null;
  }

  return {
    id: questionSnap.id,
    ...questionSnap.data(),
  } as RbQuestion;
}

/**
 * List active questions for a project
 * Ordered by orderIndex, orderSubIndex
 * 
 * Phase D5: Display current active questions in review UI
 */
export async function listActiveQuestions(
  db: Firestore,
  projectId: string
): Promise<RbQuestion[]> {
  const questionsRef = collection(db, 'rb_questions');
  const q = query(
    questionsRef,
    where('projectId', '==', projectId),
    where('isActive', '==', true),
    firestoreOrderBy('orderIndex', 'asc')
  );

  const snapshot = await getDocs(q);

  const questions = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as RbQuestion[];

  // Sort by orderIndex, then orderSubIndex (client-side for sub-index)
  return questions.sort((a, b) => {
    if (a.orderIndex !== b.orderIndex) {
      return a.orderIndex - b.orderIndex;
    }
    const aSubIndex = a.orderSubIndex ?? 0;
    const bSubIndex = b.orderSubIndex ?? 0;
    return aSubIndex - bSubIndex;
  });
}

/**
 * List all questions for a project (including superseded)
 * Used for lineage exploration
 */
export async function listAllQuestions(
  db: Firestore,
  projectId: string
): Promise<RbQuestion[]> {
  const questionsRef = collection(db, 'rb_questions');
  const q = query(
    questionsRef,
    where('projectId', '==', projectId),
    firestoreOrderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as RbQuestion[];
}

/**
 * D5.1 Edit Question
 * 
 * Creates a new question node, does not modify the existing one.
 * 
 * Rules:
 * - New node: operation='edit', lineageRootId=old.lineageRootId, 
 *   supersedesQuestionId=old.id, state='active', isActive=true
 * - Old node: set state='superseded', isActive=false
 */
export async function editQuestion(
  db: Firestore,
  newQuestionId: string,
  input: EditQuestionInput
): Promise<RbQuestion> {
  // Get the prior question
  const priorQuestion = await getQuestion(db, input.priorQuestionId);
  if (!priorQuestion) {
    throw new Error(`[rbQuestionsRepository] Prior question not found: ${input.priorQuestionId}`);
  }

  const now = Timestamp.now();

  // Create new node
  const newQuestionRef = doc(db, 'rb_questions', newQuestionId);
  const newQuestion: Omit<RbQuestion, 'id'> = {
    workspaceId: input.workspaceId,
    projectId: input.projectId,
    documentId: priorQuestion.documentId,
    orderIndex: priorQuestion.orderIndex,
    orderSubIndex: priorQuestion.orderSubIndex,
    extractedText: priorQuestion.extractedText,
    questionText: input.newQuestionText,
    state: 'active',
    isActive: true,
    operation: 'edit',
    lineageRootId: priorQuestion.lineageRootId,
    supersedesQuestionId: input.priorQuestionId,
    createdAt: now,
    createdByUserId: input.createdByUserId,
  };

  await setDoc(newQuestionRef, newQuestion);

  // Update old node
  const priorQuestionRef = doc(db, 'rb_questions', input.priorQuestionId);
  await updateDoc(priorQuestionRef, {
    state: 'superseded',
    isActive: false,
  });

  return {
    id: newQuestionId,
    ...newQuestion,
  };
}

/**
 * D5.2 Split Question
 * 
 * Split one node into multiple children.
 * 
 * Rules:
 * - Create N new nodes: operation='split', splitFromQuestionId=original.id,
 *   lineageRootId=original.lineageRootId
 * - Ordering: preserve parent's orderIndex and allocate deterministic 
 *   fractional ordering using orderSubIndex (0, 1, 2, ...)
 * - Parent becomes superseded and isActive=false
 */
export async function splitQuestion(
  db: Firestore,
  childQuestionIds: string[],
  input: SplitQuestionInput
): Promise<RbQuestion[]> {
  // Get the parent question
  const parentQuestion = await getQuestion(db, input.parentQuestionId);
  if (!parentQuestion) {
    throw new Error(`[rbQuestionsRepository] Parent question not found: ${input.parentQuestionId}`);
  }

  if (childQuestionIds.length !== input.splitTexts.length) {
    throw new Error('[rbQuestionsRepository] childQuestionIds and splitTexts must have the same length');
  }

  const now = Timestamp.now();
  const childQuestions: RbQuestion[] = [];

  // Create child nodes
  for (let i = 0; i < input.splitTexts.length; i++) {
    const childQuestionRef = doc(db, 'rb_questions', childQuestionIds[i]);
    const childQuestion: Omit<RbQuestion, 'id'> = {
      workspaceId: input.workspaceId,
      projectId: input.projectId,
      documentId: parentQuestion.documentId,
      orderIndex: parentQuestion.orderIndex,
      orderSubIndex: i, // Deterministic fractional ordering
      extractedText: parentQuestion.extractedText,
      questionText: input.splitTexts[i],
      state: 'active',
      isActive: true,
      operation: 'split',
      lineageRootId: parentQuestion.lineageRootId,
      splitFromQuestionId: input.parentQuestionId,
      createdAt: now,
      createdByUserId: input.createdByUserId,
    };

    await setDoc(childQuestionRef, childQuestion);
    childQuestions.push({
      id: childQuestionIds[i],
      ...childQuestion,
    });
  }

  // Update parent node
  const parentQuestionRef = doc(db, 'rb_questions', input.parentQuestionId);
  await updateDoc(parentQuestionRef, {
    state: 'superseded',
    isActive: false,
  });

  return childQuestions;
}

/**
 * D5.3 Merge Questions
 * 
 * Merge adjacent questions into one.
 * 
 * Rules:
 * - Create one new node: operation='merge', mergedFromQuestionIds=[...],
 *   lineageRootId = earliest orderIndex node's lineageRootId
 * - All merged nodes become superseded, isActive=false
 */
export async function mergeQuestions(
  db: Firestore,
  mergedQuestionId: string,
  input: MergeQuestionsInput
): Promise<RbQuestion> {
  // Get all questions to merge
  const questionsToMerge = await Promise.all(
    input.questionIds.map((id) => getQuestion(db, id))
  );

  // Validate all questions exist
  for (let i = 0; i < questionsToMerge.length; i++) {
    if (!questionsToMerge[i]) {
      throw new Error(`[rbQuestionsRepository] Question not found: ${input.questionIds[i]}`);
    }
  }

  // Sort by orderIndex to find earliest
  const sortedQuestions = questionsToMerge.sort((a, b) => {
    if (!a || !b) return 0;
    if (a.orderIndex !== b.orderIndex) {
      return a.orderIndex - b.orderIndex;
    }
    const aSubIndex = a.orderSubIndex ?? 0;
    const bSubIndex = b.orderSubIndex ?? 0;
    return aSubIndex - bSubIndex;
  });

  const earliestQuestion = sortedQuestions[0]!;

  const now = Timestamp.now();

  // Create merged node
  const mergedQuestionRef = doc(db, 'rb_questions', mergedQuestionId);
  const mergedQuestion: Omit<RbQuestion, 'id'> = {
    workspaceId: input.workspaceId,
    projectId: input.projectId,
    documentId: earliestQuestion.documentId,
    orderIndex: earliestQuestion.orderIndex,
    orderSubIndex: earliestQuestion.orderSubIndex,
    extractedText: earliestQuestion.extractedText,
    questionText: input.mergedText,
    state: 'active',
    isActive: true,
    operation: 'merge',
    lineageRootId: earliestQuestion.lineageRootId,
    mergedFromQuestionIds: input.questionIds,
    createdAt: now,
    createdByUserId: input.createdByUserId,
  };

  await setDoc(mergedQuestionRef, mergedQuestion);

  // Update all merged nodes to superseded
  for (const questionId of input.questionIds) {
    const questionRef = doc(db, 'rb_questions', questionId);
    await updateDoc(questionRef, {
      state: 'superseded',
      isActive: false,
    });
  }

  return {
    id: mergedQuestionId,
    ...mergedQuestion,
  };
}

/**
 * D5.4 Ignore Question
 * 
 * Mark as non-response item.
 * 
 * Rules:
 * - Create new node: operation='ignore', lineageRootId=old.lineageRootId,
 *   supersedesQuestionId=old.id, state='ignored', isActive=true
 * - Old becomes superseded, isActive=false
 */
export async function ignoreQuestion(
  db: Firestore,
  newQuestionId: string,
  input: IgnoreQuestionInput
): Promise<RbQuestion> {
  // Get the prior question
  const priorQuestion = await getQuestion(db, input.questionId);
  if (!priorQuestion) {
    throw new Error(`[rbQuestionsRepository] Question not found: ${input.questionId}`);
  }

  const now = Timestamp.now();

  // Create new node with ignored state
  const newQuestionRef = doc(db, 'rb_questions', newQuestionId);
  const newQuestion: Omit<RbQuestion, 'id'> = {
    workspaceId: input.workspaceId,
    projectId: input.projectId,
    documentId: priorQuestion.documentId,
    orderIndex: priorQuestion.orderIndex,
    orderSubIndex: priorQuestion.orderSubIndex,
    extractedText: priorQuestion.extractedText,
    questionText: priorQuestion.questionText,
    state: 'ignored',
    isActive: true,
    operation: 'ignore',
    lineageRootId: priorQuestion.lineageRootId,
    supersedesQuestionId: input.questionId,
    createdAt: now,
    createdByUserId: input.createdByUserId,
  };

  await setDoc(newQuestionRef, newQuestion);

  // Update old node
  const priorQuestionRef = doc(db, 'rb_questions', input.questionId);
  await updateDoc(priorQuestionRef, {
    state: 'superseded',
    isActive: false,
  });

  return {
    id: newQuestionId,
    ...newQuestion,
  };
}

/**
 * Count questions by state for a project
 * 
 * Phase D9: Used in Status Page
 */
export async function countQuestionsByState(
  db: Firestore,
  projectId: string
): Promise<Record<QuestionState, number>> {
  const questions = await listAllQuestions(db, projectId);

  const counts: Record<QuestionState, number> = {
    active: 0,
    ignored: 0,
    superseded: 0,
  };

  for (const question of questions) {
    counts[question.state]++;
  }

  return counts;
}

/**
 * Get total question count for a workspace (all projects)
 * 
 * Phase D9: Used in Status Page
 */
export async function getTotalQuestionCount(
  db: Firestore,
  workspaceId: string
): Promise<number> {
  const questionsRef = collection(db, 'rb_questions');
  const q = query(
    questionsRef,
    where('workspaceId', '==', workspaceId)
  );

  const snapshot = await getDocs(q);
  return snapshot.size;
}
